/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { createRoot, hydrateRoot } from 'react-dom/client';
import React, { useState, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
  useLocation
} from 'react-router-dom';
import { loadableReady } from '@loadable/component';
import { CmsContextProvider } from '@exo-provider/frontend-content-provider';
import { HelmetProvider } from 'react-helmet-async';
import { loadable } from '@exo/frontend-apps-base';
import { gql } from '@apollo/client';
import { Application } from './Application';
import applications from '../applications';
import { client } from '../applications';
import PiwikPro from '@piwikpro/react-piwik-pro';
// eslint-disable-next-line monorepo-cop/no-relative-import-outside-package
import {
  getAppLanguage,
  getBrowserLanguage,
  getSpaceDetails
} from '../../../common/utils/client/appLanguageUtils';
//import { getContentTypesSchema } from './getContentTypes';

// Provide navigation support to Cypress tests
if (typeof window !== 'undefined' && window.Cypress) {
  (window as any)._exoHistory = history;
}

const isSsr = process.env.CUSTOM_ENV?.includes('ssr');

// @ts-ignore
const _module = module?.hot;
const renderMethod =
  _module || !isSsr
    ? (app, container) => createRoot(container).render(app)
    : (app, container) => hydrateRoot(container, app);
const whenReady = isSsr ? loadableReady : cb => cb();

const GlobalModules = {
  newcleo: loadable(() => import('@exo/frontend-features-newcleo-ui/client/App'))
};

const GET_ROUTES_QUERY = gql`
  query GetRoutes($locale: String!) {
    landingPageCollection(locale: $locale) {
      items {
        name
        slug
      }
    }
    commonInnerPageCollection(locale: $locale) {
      items {
        name
        slug
      }
    }
    newsArticleCollection(locale: $locale) {
      items {
        name
        slug
      }
    }
  }
`;

const LoadingPage = () => {
  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <img src="./static/Nucleo-animation.gif" alt="Loading ..." width={150} height={150} />
    </div>
  );
};

const setLanguageBySlugLang = async () => {
  let locales = await getSpaceDetails();
  let counter = 0;
  const generatedQueries = locales.map(item => {
    const response = `
          lang${counter++}: landingPageCollection(locale: "${item.id}", where: { slug: $slug }, limit: 1) {
              items {
                name
                slug
                sys {
                  locale
                }
              }
            }
          lang${counter++}: commonInnerPageCollection(locale: "${item.id}", where: { slug: $slug }, limit: 1) {
              items {
                name
                slug
                sys {
                  locale
                }
              }
            }
          lang${counter++}: newsArticleCollection(locale: "${item.id}", where: { slug: $slug }, limit: 1) {
              items {
                name
                slug
                sys {
                  locale
                }
              }
            }
          `;
    return response;
  });
  const queryMade = `query GetRoute($slug: String!) {
      ${generatedQueries}
  }`;
  let query_data = {} as any;
  try {
    query_data = (await client.query({
      query: gql`
        ${queryMade}
      `,
      variables: { slug: location.pathname }
    })) as any;
  } catch (ex) {}

  let storageLang = localStorage.getItem('appLanguage');
  if (storageLang === 'undefined') localStorage.removeItem('appLanguage');

  let lang = {} as any;
  if (storageLang) lang = JSON.parse(storageLang)
  
  //case when locales item has id like 'fr' but in localStorage is 'fr-FR'
  if (storageLang) {
    const existsInList = locales.find(item => item.id === lang?.id);
    if(!existsInList) {{
      localStorage.removeItem('appLanguage');
      storageLang = null;
    }}
  }
  const browserLang = storageLang ? lang : getBrowserLanguage(locales);

  const listOfPagesByLang =
    Object.entries(query_data?.data || {}).map(([key, value]) => ({
      items: ((value as any).items as any[]) || ([] as any[])
    })) || ([] as any[]);

  let langByPage = '';
  const filteredPages = listOfPagesByLang.filter(langPage => langPage?.items?.length > 0) || [];
  if (filteredPages.length === 1) {
    langByPage = filteredPages[0].items[0].sys.locale || browserLang.id;
  }
  if (filteredPages.length > 1) {
    //this verification is for pages that has same slug for each language, like home: /
    let page = {} as any;
    filteredPages.forEach(value => {
      const find = value.items.find(item => item.sys.locale === browserLang.id);
      if (find) page = find;
    });
    langByPage = page?.sys?.locale || browserLang.id;
  }
  //check if current lang is equal to new one
  const newLocale = locales.find(item => item.id === langByPage);
  if (storageLang && newLocale) {
    const langFromStorage = JSON.parse(storageLang);
    if (langFromStorage.id !== newLocale.id) {
      localStorage.setItem('appLanguage', JSON.stringify(newLocale));
    }
  }

  //if no previous lang, set new one
  if (!storageLang) {
    //if some problems appeared with lang-by-slug, set the browser lang
    localStorage.setItem('appLanguage', JSON.stringify(newLocale ? newLocale : browserLang));
  }
};

const setCurrentPage = (query_data, location) => {
  const loadNewCleoModuleLanding =
    query_data?.landingPageCollection?.items.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const loadNewCleoModuleCommon =
    query_data?.commonInnerPageCollection?.items.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const loadNewCleoArticleCollection =
    query_data?.newsArticleCollection?.items.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const final = [
    ...loadNewCleoModuleLanding,
    ...loadNewCleoModuleCommon,
    ...loadNewCleoArticleCollection
  ];

  const currentPage = final.find(item => item.slug === location.pathname);
  if (currentPage) localStorage.setItem('currentPage', JSON.stringify(currentPage));
};

const Root = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  useEffect(() => {
    const fetchData = async () => {
      await setLanguageBySlugLang();
      client
        .query({ query: GET_ROUTES_QUERY, variables: { locale: getAppLanguage().id } })
        .then(({ data: query_data }) => {
          const loadNewCleoModuleLanding = query_data.landingPageCollection.items.reduce(
            (acc, slug) => {
              acc[slug.slug] = GlobalModules.newcleo;
              return acc;
            },
            {}
          );
          const loadNewCleoModuleCommon = query_data?.commonInnerPageCollection?.items?.reduce(
            (acc, slug) => {
              acc[slug.slug] = GlobalModules.newcleo;
              return acc;
            },
            {}
          );
          const loadNewCleoArticleCollection = query_data?.newsArticleCollection?.items?.reduce(
            (acc, slug) => {
              acc[slug.slug] = GlobalModules.newcleo;
              return acc;
            },
            {}
          );
          setCurrentPage(query_data, location);

          applications.bundles[0].modules = {
            ...applications.bundles[0].modules,
            ...loadNewCleoModuleLanding,
            ...loadNewCleoModuleCommon,
            ...loadNewCleoArticleCollection
          };
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
          setError(err);
        });
    };
    fetchData();
  }, [location]);
  PiwikPro.initialize('3b5dae09-e3c6-41b5-86a4-de59010bb5e8', 'https://newcleo.piwik.pro');
  return (
    <>
      {loading ? <LoadingPage /> : null}
      {!loading && !error ? (
        <>
          <ScrollRestoration />
          <Application config={applications} />
        </>
      ) : null}
    </>
  );
};

const router = createBrowserRouter([{ path: '*', element: <Root /> }]);

const promises = CmsContextProvider?.globalCmsInit?.(applications) ?? [];
Promise.all(promises).then(() => {
  whenReady(() => {
    renderMethod(
      <HelmetProvider>
        <RouterProvider router={router} />
      </HelmetProvider>,
      document.getElementById('root-newcleo')
    );
  });
});
